'use client';

import { useMutation } from '@tanstack/react-query';
import type { DocumentNode } from 'graphql';

import { requestGraphQL, requestGraphQLWithResponse } from '../client';

export type UseLazyGraphQLQueryOptions<TData, TVariables = Record<string, unknown>> = {
  query: DocumentNode | string;
  /** When true, uses requestGraphQLWithResponse and exposes responseHeaders */
  withHeaders?: boolean;
};

export function useLazyGraphQLQuery<TData, TVariables = Record<string, unknown>>({
  query,
  withHeaders = false,
}: UseLazyGraphQLQueryOptions<TData, TVariables>) {
  const mutation = useMutation({
    mutationFn: async (variables?: TVariables) => {
      if (withHeaders) {
        const { data, headers } = await requestGraphQLWithResponse<TData, TVariables>(
          query,
          variables
        );
        return { data, headers };
      }
      const data = await requestGraphQL<TData, TVariables>(query, variables);
      return { data };
    },
    gcTime: 0,
  });

  const responseData = mutation.data as { data: TData; headers?: Headers } | undefined;
  const data = responseData?.data;
  const responseHeaders = responseData?.headers;

  const execute = (variables?: TVariables) => {
    mutation.mutate(variables as TVariables);
  };

  const result = {
    data,
    loading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    ...(withHeaders && { responseHeaders }),
  };

  return [execute, result] as const;
}
